import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Link,
  Paper,
  Grid,
  Chip,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  AssignmentOutlined,
  LabelOutlined,
  TrackChanges,
  FormatQuote,
  LightbulbOutlined,
  AutoAwesome,
  EditOutlined,
  InfoOutlined,
  CheckOutlined,
  CalendarTodayOutlined,
  PersonOutlined,
  FolderOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import { API_CONFIG, buildEndpoint } from '../../config/api.config';
import apiClient from '../../services/apiClient';

const formatDateTime = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const formatLabel = (value) => {
  if (!value) return '-';
  return String(value).replace(/_/g, ' ');
};

const getFeedbackFromResponse = (payload) => {
  if (!payload) return null;
  if (payload.llmFeedback) return payload.llmFeedback;
  if (payload.feedback) return payload.feedback;
  if (payload.data?.llmFeedback) return payload.data.llmFeedback;
  if (payload.data?.feedback) return payload.data.feedback;
  if (payload.data && !Array.isArray(payload.data)) return payload.data;
  return payload;
};

const FeedbackTriage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [category, setCategory] = useState('');
  const [ruleText, setRuleText] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadFeedbackDetail = async () => {
      if (!id) {
        setLoading(false);
        setErrorMessage('Feedback id is required.');
        return;
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const endpoint = buildEndpoint(API_CONFIG.dcr.getLlmFeedbackById.endpoint, {
          feedbackId: id,
        });
        const response = await apiClient.get(endpoint);
        if (!isMounted) return;

        const feedbackRecord = getFeedbackFromResponse(response.data);
        setFeedback(feedbackRecord);
        setCategory(feedbackRecord?.category || feedbackRecord?.feedback_type || '');
        setRuleText(feedbackRecord?.suggested_rule || feedbackRecord?.rule_text || '');
      } catch (error) {
        if (!isMounted) return;
        setErrorMessage(
          error?.response?.data?.message || error?.message || 'Unable to load feedback detail.'
        );
        setFeedback(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadFeedbackDetail();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleDiscard = () => {
    navigate('/feedback');
  };

  const handleSave = () => {
    navigate('/feedback');
  };

  const feedbackId = feedback?.feedback_id || feedback?.id || id || '-';
  const workflowName = feedback?.workflow_name || '-';
  const username = feedback?.username || feedback?.created_by || '-';
  const requestId = feedback?.request_id || '-';
  const slotKey = feedback?.slot_key || feedback?.target || '-';
  const comment = feedback?.comment || feedback?.feedback || '-';
  const reaction = feedback?.reaction || '-';
  const status = feedback?.status || '-';
  const createdAt = formatDateTime(feedback?.created_at);
  const selectedCategory = category || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
      <Box sx={{ flex: 1, mb: 2 }}>
        <Box sx={{ mb: 1 }}>
          <PageBreadcrumbs
            items={[
              { label: 'Feedback Inbox', path: '/feedback' },
              { label: `Feedback #${feedbackId}`, path: null },
            ]}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              minHeight: '72vh',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              bgcolor: 'white',
              p: { xs: 2, sm: 3, md: 3 },
            }}
          >
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            {loading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : !feedback ? (
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                No data found.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 2.5 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: '#f0f9ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AssignmentOutlined sx={{ fontSize: 28, color: '#1d8ef0' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        mb: 0.5,
                        color: '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      Feedback #{feedbackId}
                      <Chip
                        label={formatLabel(reaction)}
                        size="small"
                        sx={{
                          bgcolor: '#fee2e2',
                          color: '#ef4444',
                          fontWeight: 600,
                          borderRadius: '16px',
                          height: 24,
                          mb: 0.5,
                          border: 'none',
                          textTransform: 'capitalize',
                        }}
                      />
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={2.5} flexWrap="wrap">
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#64748b' }}>
                        <SettingsOutlined sx={{ fontSize: 13 }} />
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          {workflowName}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#64748b' }}>
                        <PersonOutlined sx={{ fontSize: 13 }} />
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          {username}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#64748b' }}>
                        <FolderOutlined sx={{ fontSize: 13 }} />
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          Request {requestId}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#64748b' }}>
                        <CalendarTodayOutlined sx={{ fontSize: 13 }} />
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          Submitted {createdAt}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Box>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#ffffff',
                        height: '100%',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: '#f0f9ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <TrackChanges sx={{ fontSize: 18, color: '#1d8ef0' }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                          Target
                        </Typography>
                      </Stack>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                          {formatLabel(slotKey)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          slot_key: {slotKey}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          bgcolor: '#f8fafc',
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <FormatQuote sx={{ color: '#1d8ef0', mr: 1, transform: 'scaleX(-1)' }} />
                        <Typography variant="body2" sx={{ color: '#334155', fontStyle: 'italic', pt: 0.5 }}>
                          {comment}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#ffffff',
                        height: '100%',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: '#f0f9ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <LabelOutlined sx={{ fontSize: 18, color: '#1d8ef0' }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                          Category
                        </Typography>
                      </Stack>

                      <Select
                        fullWidth
                        size="small"
                        value={selectedCategory}
                        displayEmpty
                        onChange={(e) => setCategory(e.target.value)}
                        sx={{
                          bgcolor: '#ffffff',
                          minHeight: 38,
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                          mb: 2,
                          color: '#64748b',
                        }}
                      >
                        <MenuItem value="">-</MenuItem>
                        <MenuItem value="agent_prompting">Agent Prompting</MenuItem>
                        <MenuItem value="feedback_as_context">Feedback as Context</MenuItem>
                        <MenuItem value="fine_tuning">Fine-Tuning</MenuItem>
                      </Select>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          Status:
                        </Typography>
                        <Chip
                          label={formatLabel(status)}
                          size="small"
                          sx={{
                            bgcolor: '#f1f5f9',
                            color: '#64748b',
                            fontWeight: 700,
                            borderRadius: '16px',
                            height: 24,
                            border: 'none',
                            textTransform: 'capitalize',
                          }}
                        />
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
                <Box>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                      <AutoAwesome sx={{ color: '#1d8ef0', fontSize: 18 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        AI-suggested rule
                      </Typography>
                      <Chip
                        label="-"
                        size="small"
                        sx={{
                          bgcolor: '#f0f9ff',
                          color: '#1d8ef0',
                          fontWeight: 500,
                          border: 'none',
                          height: 24,
                        }}
                      />
                    </Stack>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.3,
                        mb: 3,
                        bgcolor: '#fffbeb',
                        border: '1px solid #fcd34d',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <LightbulbOutlined sx={{ color: '#d97706', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#92400e' }}>
                        {feedback?.resolved_rule_id ? `Resolved rule: ${feedback.resolved_rule_id}` : '-'}
                      </Typography>
                    </Paper>

                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={ruleText}
                        placeholder="-"
                        onChange={(e) => setRuleText(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                            p: 2,
                            pb: 6,
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                          },
                        }}
                      />
                      <Box sx={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 1 }}>
                        <IconButton size="small" sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 0.5 }}>
                          <AutoAwesome sx={{ fontSize: 16, color: '#1d8ef0' }} />
                        </IconButton>
                        <IconButton size="small" sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 0.5 }}>
                          <EditOutlined sx={{ fontSize: 16, color: '#64748b' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#94a3b8' }}>
                        <InfoOutlined sx={{ fontSize: 16 }} />
                        <Typography variant="caption">-</Typography>
                      </Stack>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={handleDiscard}
                          sx={{
                            textTransform: 'none',
                            color: '#334155',
                            borderColor: '#e2e8f0',
                            px: 4,
                            fontWeight: 600,
                            '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                          }}
                        >
                          Discard
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSave}
                          startIcon={<CheckOutlined />}
                          sx={{
                            textTransform: 'none',
                            bgcolor: '#1d8ef0',
                            px: 3,
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#187acb', boxShadow: 'none' },
                          }}
                        >
                          Approve & Save Rule
                        </Button>
                      </Box>
                    </Box>
                  </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default FeedbackTriage;